// 2) Hooks — useSiteData.js
import { useQuery } from "@tanstack/react-query";
import { FinanceApi } from "api/finance/service";
import { WorkersApi } from "api/workers/service";
import { MaterialsApi } from "api/materials/service";
import { ProjectApi } from "api/projects/service";
import { taskApi } from "api/tasks/taskApi";
import { rtdb } from "utils/firebase";
import { BASE_URL, environment } from "utils/constant";


const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const useSite = (siteId) =>
  useQuery({
    queryKey: ["site", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await ProjectApi.getSiteById(siteId);
      if (!res?.status) throw new Error(res?.message || "Site fetch failed");
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

export const useOwnerProjectSummary = (organizationId, siteId) =>
  useQuery({
    queryKey: ["ownerProjectSummary", organizationId, siteId],
    enabled: !!organizationId && !!siteId,
    queryFn: async () => {
      const res = await ProjectApi.getProjectSummary(organizationId, siteId);
      if (!res?.status) throw new Error(res?.message || "Project summary fetch failed");
      return res.data || null;
    },
    retry: 1,
    placeholderData: (prev) => prev,
  });

export const useIndividualProjectSummary = (organizationId, siteId) =>
  useQuery({
    queryKey: ["individualProjectSummary", organizationId, siteId],
    enabled: !!organizationId && !!siteId,
    queryFn: async () => {
      const res = await ProjectApi.getIndividualProjectSummary(organizationId, siteId);
      if (!res?.status) throw new Error(res?.message || "Individual project summary fetch failed");
      return res.data || null;
    },
    retry: 1,
    placeholderData: (prev) => prev,
  });

export const useFinance = (siteId) =>
  useQuery({
    queryKey: ["finance", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const [allRes, pendingRes] = await Promise.all([
        FinanceApi.listAllFinancebySite({ sites: siteId }),
        FinanceApi.listPendingTransactionsBySite({ sites: siteId }),
      ]);

      if (!allRes?.status) throw new Error("Finance fetch failed");

      return {
        allTransactions: allRes.data || [],
        pendingTransactions: pendingRes?.status ? pendingRes : { pending_material_transactions: [], pending_worker_transactions: [] },
      };
    },
    select: ({ allTransactions, pendingTransactions }) => {
      const categoryNames =
        JSON.parse(localStorage.getItem("categoryNames")) || {};
      const materialCategory = localStorage.getItem("materialCategory") || null;
      const otherCategory = localStorage.getItem("otherCategory") || null;
      const workerCategory = localStorage.getItem("workerCategory") || null;
      const contractorPaymentCategory = localStorage.getItem("contractorPaymentCategory") || null;
      const refundCategory = localStorage.getItem("refundCategory") || null;
      const receivedCategory = localStorage.getItem("receivedCategory") || null;

      const cat = {};
      for (const id of Object.keys(categoryNames)) {
        cat[id] = 0;
      }

      allTransactions.forEach((t) => {
        const key = t.transaction_catagory_id;
        const amt = t?.amount ?? t?.paid_amount ?? 0;
        if (key in cat) cat[key] += amt;
      });

      let totalDue = 0;
      // Calculate totalDue from pending transactions API
      const pendingMaterials = pendingTransactions.pending_material_transactions || [];
      const pendingWorkers = pendingTransactions.pending_worker_transactions || [];

      pendingMaterials.forEach((t) => {
        totalDue += (t.remaining_payment || 0);
      });
      pendingWorkers.forEach((t) => {
        totalDue += (t.pending_amount || 0);
      });

      const totalExpense =
        (cat[materialCategory] || 0) +
        (cat[workerCategory] || 0) +
        (cat[otherCategory] || 0) +
        (cat[contractorPaymentCategory] || 0)
        - (cat[refundCategory] || 0);
      const totalReceived = cat[receivedCategory] || 0;
      const remainingAmount = totalReceived - totalExpense;

      const now = new Date();
      const last8Keys = Array.from({ length: 8 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (7 - i), 1);
        return `${d.toLocaleString("default", {
          month: "short",
        })} ${d.getFullYear()}`;
      });

      const grouped = {};
      for (const t of allTransactions) {
        const dt = new Date(t.transaction_date || t.date);
        const key = `${dt.toLocaleString("default", {
          month: "short",
        })} ${dt.getFullYear()}`;
        grouped[key] = (grouped[key] ?? 0) + (t.amount || t.total_price || 0);
      }
      const cashFlowData = last8Keys.map((k) => ({
        name: k,
        Amount: grouped[k] || 0,
      }));

      return {
        list: allTransactions,
        totals: { totalExpense, totalReceived, totalDue, remainingAmount },
        cashFlowData,
      };
    },
    placeholderData: (prev) => prev,
  });

export const useAttendanceLast7 = (siteId) =>
  useQuery({
    queryKey: ["attendance", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await WorkersApi.listAllWorkerAttendance({ site_id: siteId });
      if (!res?.status || !Array.isArray(res.data)) return [];
      return res.data;
    },
    select: (rows) => {
      const today = new Date();
      const last7 = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        return {
          dateStr: ymd(d),
          label: d.toLocaleDateString("en-US", { weekday: "short" }),
        };
      }).reverse();

      const count = {};
      for (const r of rows) {
        const day = r.attendance_date;
        if (!count[day]) count[day] = { Present: 0, Absent: 0 };
        if (r.attendance === "Present") count[day].Present += 1;
        else if (r.attendance === "Absent") count[day].Absent += 1;
      }

      const labourSeries = last7.map(({ dateStr, label }) => ({
        name: label,
        Present: count[dateStr]?.Present ?? 0,
        Absent: count[dateStr]?.Absent ?? 0,
      }));

      const recent = [...rows].reverse(); // for "recent attendance log"
      return { labourSeries, recent };
    },
    placeholderData: (prev) => prev,
  });

export const useWorkers = (siteId) =>
  useQuery({
    queryKey: ["workers", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const [w, c] = await Promise.allSettled([
        WorkersApi.listAllWorkers({ sites: siteId, types: "Worker" }),
        WorkersApi.listAllWorkers({ sites: siteId, types: "Crew" }),
      ]);
      const arr = [];
      if (w.status === "fulfilled" && w.value?.status)
        arr.push(...(w.value.data || []));
      if (c.status === "fulfilled" && c.value?.status)
        arr.push(...(c.value.data || []));
      return arr;
    },
    placeholderData: (prev) => prev,
  });

export const useMaterials = (siteId) =>
  useQuery({
    queryKey: ["materials", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await MaterialsApi.listAllMaterialsBySite({ sites: siteId });
      if (!res?.status) throw new Error("Materials fetch failed");
      return res.data || [];
    },
    placeholderData: (prev) => prev,
  });

export const useMaterialRequests = (siteId) =>
  useQuery({
    queryKey: ["materialRequests", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const token = localStorage.getItem("access");
      const resp = await fetch(
        `${BASE_URL}/inventory/material-requests/site/${siteId}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!resp.ok) throw new Error("Material requests fetch failed");
      const json = await resp.json();
      return json?.data || [];
    },
    placeholderData: (prev) => prev,
  });

export const useDeliveries = (siteId) =>
  useQuery({
    queryKey: ["deliveries", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const token = localStorage.getItem("access");
      const resp = await fetch(
        `${BASE_URL}/inventory/get-deliveries-of-a-site/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sites: siteId }),
        }
      );
      if (!resp.ok) throw new Error("Deliveries fetch failed");
      const json = await resp.json();
      return json?.data || [];
    },
    placeholderData: (prev) => prev,
  });

export const useSiteMembers = (siteId) =>
  useQuery({
    queryKey: ["siteMembers", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await ProjectApi.listSiteMembers(siteId);
      if (!res?.status) throw new Error(res?.message || "Members fetch failed");
      return res.data || [];
    },
    placeholderData: (prev) => prev,
  });

export const useTaskGroupsWithTasks = (siteId) =>
  useQuery({
    queryKey: ["taskGroupsWithTasks", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const token = localStorage.getItem("access");
      const resp = await fetch(
        `${BASE_URL}/task/get-all-task-groups/?site_id=${encodeURIComponent(
          siteId
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
      const json = await resp.json();
      if (!json?.status || !Array.isArray(json.data))
        throw new Error("Invalid task groups");

      const groups = json.data;

      const results = await Promise.allSettled(
        groups.map(async (g) => {
          try {
            const tasks = await taskApi.getAllTasks(g.id);
            return { group: g, tasks: Array.isArray(tasks) ? tasks : [] };
          } catch {
            return { group: g, tasks: [] };
          }
        })
      );

      const combined = results.map((r, i) =>
        r.status === "fulfilled" ? r.value : { group: groups[i], tasks: [] }
      );

      const summary = groups.reduce(
        (acc, g) => {
          const s = g?.task_group_summary || {};
          acc.total_tasks += Number(s.total_tasks || 0);
          acc.completed_tasks += Number(s.completed_tasks || 0);
          acc.ongoing_tasks += Number(s.ongoing_tasks || 0);
          acc.overdue_tasks += Number(s.overdue_tasks || 0);
          return acc;
        },
        {
          total_tasks: 0,
          completed_tasks: 0,
          ongoing_tasks: 0,
          overdue_tasks: 0,
        }
      );

      return { combined, summary };
    },
    placeholderData: (prev) => prev,
  });

export const useWeather = (city, apiKey) =>
  useQuery({
    queryKey: ["weather", city],
    enabled: !!city && !!apiKey,
    queryFn: async () => {
      const resp = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(
          city
        )}&appid=${apiKey}&units=metric`
      );
      if (!resp.ok) throw new Error("Weather data not available");
      return resp.json();
    },
    staleTime: 10 * 60_000,
    placeholderData: (prev) => prev,
  });

export const useGPSPreference = (siteId, refreshKey) =>
  useQuery({
    queryKey: ["gpsPref", siteId, refreshKey],
    enabled: !!siteId,
    queryFn: async () => {
      const snap = await rtdb
        .ref(`GPSPreferences-${environment}/Sites/${siteId}`)
        .once("value");
      const val = snap.val();
      return val !== null ? !!val : false;
    },
    placeholderData: (prev) => prev,
  });

export const useTaskReport = (siteId) =>
  useQuery({
    queryKey: ["taskReport", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await taskApi.getTaskReport(siteId);
      if (!res?.status) throw new Error(res?.message || "Task report fetch failed");
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

export const useWorkerReport = (siteId) =>
  useQuery({
    queryKey: ["workerReport", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const res = await WorkersApi.getWorkerReport(siteId);
      if (!res?.status) throw new Error(res?.message || "Worker report fetch failed");
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
